import RowItemWidget from './rowItemWidget'
import {
  ACTION_FETCH_NEED_ATTENTION_DATA,
  NeedAttentionData,
  fetchNeedAttentionData
} from '../privacyOpsOverviewSlice'
import { getShowLoader } from '../../../reducers/requestReducer'
import { RootState } from '../../../rootReducer'
import {
  ACTION_FETCH_COOKIE_CONSENT_DOMAIN_SCAN_STATS,
  CookieConsentDomain,
  CookieConsentDomainsParams,
  CookieConsentScanStatsParams,
  fetchCookieConsentDomains
} from '../../cookieConsent/domains/domainsSlice'
import { getCreatedDateUrlFilter } from '../../dsr/utils'
import { dateMaps } from '../../cookieConsent/overview/constants'
import { getUpdatedUrl } from '../../../utils/urlUtil'
import {
  DSR_FILTER_MANUAL_ACTION,
  FILTER_CREATED_AT,
  FILTER_NEED_MANUAL_ACTION,
  FILTER_REVIEW_STATUS,
  PAGE_VIEW_KEY,
  PAGE_VIEW_VALUES,
  PiaProcessFilter,
  ProcessReviewStatus,
  URL_COOKIES_CONSENT,
  URL_DEFINED_PROCESSES,
  URL_DOMAINS,
  URL_DSR,
  URL_DSR_REQUEST_MANAGEMENT,
  URL_PIA,
  URL_ROPA
} from '../../../constants'
import { useIntl } from 'react-intl'
import React, { useEffect, useMemo } from 'react'
import { connect } from 'react-redux'

interface IProps {
  showLoader: boolean
  needAttentionData: NeedAttentionData
  domains: CookieConsentDomain[]
  timePeriod?: string
  fetchNeedAttentionData: (timePeriod: string) => void
  fetchCookieConsentDomainScanStats: (params?: CookieConsentScanStatsParams) => void
  fetchCookieConsentDomains: (params: CookieConsentDomainsParams) => void
}

const NeedAttentionWidget = ({
  showLoader,
  needAttentionData,
  domains,
  timePeriod = '',
  fetchNeedAttentionData,
  fetchCookieConsentDomains
}: IProps) => {
  const intl = useIntl()

  const createdDateUrlFilter = useMemo(() => getCreatedDateUrlFilter(timePeriod), [timePeriod])

  useEffect(() => {
    fetchNeedAttentionData(timePeriod)
  }, [fetchNeedAttentionData, timePeriod])

  useEffect(() => {
    fetchCookieConsentDomains({
      include_cookies: true,
      include_jobs: true,
      duration: dateMaps[timePeriod]
    })
  }, [fetchCookieConsentDomains, timePeriod])

  const domainsNeedAttention = useMemo(
    () => domains.filter((domain) => domain.hasCookieCollision),
    [domains]
  )

  const widgetData = useMemo(
    () =>
      Object.keys(needAttentionData).length
        ? [
            {
              key: intl.formatMessage({ id: 'po.dashboard.needAttention.widget.line1' }),
              value: needAttentionData.dsrCount,
              link: getUpdatedUrl({
                pathName: `${URL_DSR}${URL_DSR_REQUEST_MANAGEMENT}`,
                pageName: URL_DSR_REQUEST_MANAGEMENT,
                pageQueryParams: {
                  [FILTER_NEED_MANUAL_ACTION]: DSR_FILTER_MANUAL_ACTION.NEEDED,
                  [FILTER_CREATED_AT]: createdDateUrlFilter
                }
              })
            },
            {
              key: intl.formatMessage({ id: 'po.dashboard.needAttention.widget.line2' }),
              value: needAttentionData.piaAssessmentCount,
              link: getUpdatedUrl({
                pathName: `${URL_PIA}${URL_DEFINED_PROCESSES}`,
                pageName: URL_DEFINED_PROCESSES,
                pageQueryParams: {
                  [PAGE_VIEW_KEY]: PAGE_VIEW_VALUES.list,
                  [FILTER_REVIEW_STATUS]: PiaProcessFilter.reviewPending
                }
              })
            },
            {
              key: intl.formatMessage({ id: 'po.dashboard.needAttention.widget.line3' }),
              value: domainsNeedAttention.length,
              link: `${URL_COOKIES_CONSENT}${URL_DOMAINS}`
            },
            {
              key: intl.formatMessage({ id: 'po.dashboard.needAttention.widget.line4' }),
              value: needAttentionData.ropaProcessCount,
              link: getUpdatedUrl({
                pathName: `${URL_ROPA}${URL_DEFINED_PROCESSES}`,
                pageName: URL_DEFINED_PROCESSES,
                pageQueryParams: {
                  [PAGE_VIEW_KEY]: PAGE_VIEW_VALUES.list,
                  [FILTER_REVIEW_STATUS]: [
                    ProcessReviewStatus.reviewDue,
                    ProcessReviewStatus.reviewPastDue
                  ].join(',')
                }
              })
            }
          ]
        : [],
    [needAttentionData, domainsNeedAttention]
  )

  return (
    <RowItemWidget
      widgetData={widgetData}
      title={intl.formatMessage({ id: 'po.dashboard.needAttention.widget.title' })}
      showLoader={showLoader}
    />
  )
}

const mapStateToProps = (state: RootState) => ({
  needAttentionData: state.privacyOpsOverview.needAttentionData,
  domains: state.domains.domains.list,
  timePeriod: state.customDashboards.filterProps?.timePeriod,
  showLoader:
    getShowLoader(ACTION_FETCH_NEED_ATTENTION_DATA) ||
    getShowLoader(ACTION_FETCH_COOKIE_CONSENT_DOMAIN_SCAN_STATS)
})
const mapDispatchToProps = { fetchNeedAttentionData, fetchCookieConsentDomains }

export default connect(mapStateToProps, mapDispatchToProps)(NeedAttentionWidget)
